import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Coins,
  Globe,
  HandCoins,
  KeyRound,
  Lock,
  Network,
  Palette,
  type LucideIcon
} from 'lucide-react'
import { Select, Switch } from '@radix-ui/themes'
import { useCurrency } from '@/hooks/useCurrency'
import FutureFunctionDialog from '@/components/dialogs/FutureFunctionDialog'
import PhraseSecurityDialog from '@/components/dialogs/PhraseSecurityDialog'
import ChangePasswordDialog from '@/components/dialogs/ChangePasswordDialog'
import ManageAssetsDialog from '@/components/dialogs/ManageAssetsDialog'
import Snackbar from '@/components/Snackbar'
import SettingsActionCard from '@/components/SettingsActionCard'
import { useTranslation } from 'react-i18next'
import { LANGUAGE_OPTIONS as languageOptions } from '@/config/languages'

const Settings = () => {
  const { t, i18n } = useTranslation()
  const { selectedCurrency, changeCurrency, currencies } = useCurrency()
  const navigate = useNavigate()

  const [openDialog, setOpenDialog] = useState(false)
  const [openPhraseSecurityDialog, setOpenPhraseSecurityDialog] =
    useState(false)
  const [openChangePasswordGate, setOpenChangePasswordGate] = useState(false)
  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false)
  const [openManageAssetsDialog, setOpenManageAssetsDialog] = useState(false)
  const [verifiedPassword, setVerifiedPassword] = useState('')
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({ isOpen: false, message: '', type: 'info' })

  const toggleDark = () => {
    document.documentElement.classList.toggle('dark')
  }

  const selectedLanguage =
    languageOptions.find((language) =>
      i18n.language.toLowerCase().startsWith(language.code)
    )?.code ?? 'en'

  const accountActionCards: {
    icon: LucideIcon
    titleKey: string
    descriptionKey: string
    onClick: () => void
  }[] = [
    {
      icon: Network,
      titleKey: 'settings.networks',
      descriptionKey: 'settings.view_networks',
      onClick: () => navigate('/networks')
    },
    {
      icon: Coins,
      titleKey: 'settings.manage_assets',
      descriptionKey: 'settings.manage_assets_desc',
      onClick: () => setOpenManageAssetsDialog(true)
    },
    {
      icon: KeyRound,
      titleKey: 'settings.recover_wallet',
      descriptionKey: 'settings.view_phrase',
      onClick: () => setOpenPhraseSecurityDialog(true)
    },
    {
      icon: Lock,
      titleKey: 'settings.change_password',
      descriptionKey: 'settings.change_password_desc',
      onClick: () => setOpenChangePasswordGate(true)
    }
  ]

  return (
    <div className="flex flex-col h-full w-full gap-10">
      <div className="flex flex-col gap-3 pt-4">
        <h1 className="text-4xl font-extrabold text-text-main">
          {t('settings.title')}
        </h1>
        <p className="text-text-secondary">{t('settings.desc')}</p>
      </div>

      {/* ACCOUNT */}
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-10">
        <div className="flex flex-col gap-4 lg:w-[60%]">
          <p className="text-sm text-contrast font-bold uppercase tracking-[2.4px]">
            {t('settings.account')}
          </p>

          {accountActionCards.map((card) => (
            <SettingsActionCard
              key={card.titleKey}
              icon={card.icon}
              title={t(card.titleKey)}
              description={t(card.descriptionKey)}
              onClick={card.onClick}
            />
          ))}

          <ManageAssetsDialog
            open={openManageAssetsDialog}
            onClose={() => setOpenManageAssetsDialog(false)}
          />
          <PhraseSecurityDialog
            open={openPhraseSecurityDialog}
            onClose={() => setOpenPhraseSecurityDialog(false)}
          />
          <PhraseSecurityDialog
            open={openChangePasswordGate}
            onClose={() => setOpenChangePasswordGate(false)}
            onVerified={(password) => {
              setVerifiedPassword(password)
              setOpenChangePasswordDialog(true)
            }}
          />
          <ChangePasswordDialog
            open={openChangePasswordDialog}
            oldPassword={verifiedPassword}
            onClose={() => {
              setVerifiedPassword('')
              setOpenChangePasswordDialog(false)
            }}
            onSuccess={() => {
              setVerifiedPassword('')
              setOpenChangePasswordDialog(false)
              setSnackbarState({
                isOpen: true,
                message: t('settings.change_password_success'),
                type: 'success'
              })
            }}
            onError={(message) => {
              setSnackbarState({ isOpen: true, message, type: 'error' })
            }}
          />

          {/* <button className="flex flex-col gap-2 bg-card rounded-xl p-5 items-start">
          <div className="bg-card-e2 rounded-full items-center max-w-fit p-4">
            <FingerprintPattern className="w-6 h-6 text-primary" />
          </div>
          <p className="text-text-main font-semibold">Biometry</p>
          <p className="text-text-secondary text-sm">
            FaceID o huella dactilar activa
          </p>
        </button>
        <button className="flex flex-col gap-2 bg-card rounded-xl p-5 items-start">
          <div className="bg-card-e2 rounded-full items-center max-w-fit p-4">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <p className="text-text-main font-semibold">2FA Security</p>
          <p className="text-text-secondary text-sm">
            Protección de doble factor
          </p>
        </button> */}
        </div>

        {/* APP PREFERENCES */}
        <div className="w-full flex flex-col gap-7 lg:gap-8">
          <div className="flex flex-col gap-5">
            <p className="text-sm text-contrast font-semibold uppercase tracking-[2.4px]">
              {t('settings.app_preferences')}
            </p>

            <div className="flex flex-col rounded-2xl shadow-sm bg-card">
              <div className="flex justify-between items-center gap-2 px-4 py-5 w-full border-border-base ">
                <div className="flex gap-3 items-center">
                  <Palette className="w-5 h-5 text-text-secondary" />
                  <p className="text-text-main">{t('settings.toggle_theme')}</p>
                </div>
                <Switch defaultChecked onClick={toggleDark} color="amber" />
              </div>
              <div className="flex justify-between items-center gap-2 px-4 py-5 w-full border-border-base border-t">
                <div className="flex gap-3 items-center">
                  <HandCoins className="w-5 h-5 text-text-secondary" />
                  <p className="text-text-main">
                    {t('settings.main_currency')}
                  </p>
                </div>
                <Select.Root
                  value={selectedCurrency}
                  onValueChange={changeCurrency}
                >
                  <Select.Trigger radius="full" color="amber" variant="soft" />
                  <Select.Content color="amber">
                    {currencies.map((c) => (
                      <Select.Item key={c.code} value={c.code}>
                        {c.code.toUpperCase()} - {c.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </div>
              <div className="flex justify-between items-center gap-2 px-4 py-5 w-full border-border-base border-t ">
                <div className="flex gap-3 items-center">
                  <Globe className="w-5 h-5 text-text-secondary" />
                  <p className="text-text-main">{t('settings.language')}</p>
                </div>
                <Select.Root
                  value={selectedLanguage}
                  onValueChange={(val) => i18n.changeLanguage(val)}
                >
                  <Select.Trigger radius="full" variant="ghost" color="amber" />
                  <Select.Content color="amber">
                    {languageOptions.map((language) => (
                      <Select.Item key={language.code} value={language.code}>
                        {language.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </div>
              {/* <div className="flex justify-between items-center gap-2 px-4 py-5 w-full border-border-base border-t ">
            <div className="flex gap-3 items-center">
              <Mail className="w-5 h-5 text-text-secondary" />
              <p className="text-text-main">Email Reports</p>
            </div>
            <Switch defaultChecked color="amber" />
          </div> */}
            </div>
          </div>

          {/* SUPPORT — oculto a pedido, código queda para reactivar después
          <div className="flex flex-col gap-3">
            <p className="text-sm text-contrast font-semibold uppercase tracking-[2.4px]">
              {t('settings.support')}
            </p>
            <button
              onClick={() =>
                window.open('https://support.onemillionsdollar.net/', '_blank')
              }
              className="w-full bg-card dark:bg-app shadow-sm p-5 rounded-xl border border-border-base flex justify-between"
            >
              <div className="flex items-center gap-3">
                <HandHelping className="w-5 h-5 icon-gradient-secondary icon-gradient-primary-dark" />
                <span className="text-text-main font-medium">
                  {t('settings.help_center')}
                </span>
              </div>
              <SquareArrowOutUpRight className="text-text-secondary w-5 h-5" />
            </button>

            <button
              className="w-full bg-card dark:bg-app shadow-sm p-5 rounded-xl border border-border-base flex justify-between "
              onClick={() => setOpenDialog(true)}
            >
              <div className="flex items-center gap-3">
                <Gavel className="w-5 h-5 icon-gradient-secondary icon-gradient-primary-dark" />
                <span className="text-text-main font-medium">
                  {t('settings.terms')}
                </span>
              </div>
              <ChevronRight className="text-text-secondary w-5 h-5" />
            </button>
          </div>
          */}
        </div>
      </div>
      <FutureFunctionDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />
      <Snackbar
        isOpen={snackbarState.isOpen}
        message={snackbarState.message}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Settings
